/* eslint-disable import/prefer-default-export */
import { configureStore } from '@reduxjs/toolkit'
import logger from 'redux-logger'
import { fromJS } from 'immutable'
import _assign from 'lodash/assign'
import _concat from 'lodash/concat'
import rootReducer from '../reducers/index'
import { createMiddleware as createWitnessMiddleware } from '../redux-witness/index'
import authMiddleware from '../services/auth/middleware'
import assetsMiddleware from '../services/assets/middleware'
import eventTrackingMiddleware from '../services/event-tracking/middleware'
import userMiddleware from '../services/user/middleware'
import dispatchWhenMiddleware from '../services/dispatch-when/middleware'
import optimizelyMiddleware from '../services/optimizely/middleware'
import optimizelyFeatureMiddleware from '../services/optimizely-feature/middleware'
import dialogMiddleware from '../services/dialog/middleware'
import featureTrackingMiddleware from '../services/feature-tracking/middleware'
import paytrackMiddleware from '../services/paytrack/middleware'
import searchMiddleware from '../services/search/middleware'
import liveAccessEventsMiddleware from '../services/live-access-events/middleware'
import userNodeInfoMiddleware from '../services/user-node-info/middleware'
import onboardingMiddleware from '../services/onboarding/middleware'
import guidesMiddleware from '../services/guides/middleware'
import guideDaysMiddleware from '../services/guide-days/middleware'
// new middleware pattern for Redux-Witness
import * as abuseWatchers from '../services/abuse/watchers'
import * as adyenWatchers from '../services/adyen/watchers'
import * as alertBarWatchers from '../services/alert-bar/watchers'
import * as ambassadorsWatchers from '../services/ambassadors/watchers'
import * as appWatchers from '../services/app/watchers'
import * as authWatchers from '../services/auth/watchers'
import * as checkoutWatchers from '../services/checkout/watchers'
import * as cookieWatchers from '../services/cookie/watchers'
import * as detailWatchers from '../services/detail/watchers'
import * as upstreamContextWatchers from '../services/upstream-context/watchers'
import * as dialogWatchers from '../services/dialog/watchers'
import * as emailSignupWatchers from '../services/email-signup/watchers'
import * as eventTrackingWatchers from '../services/event-tracking/watchers'
import * as filterSetWatchers from '../services/filter-set/watchers'
import * as giftWatchers from '../services/gift/watchers'
import * as hiddenContentWatchers from '../services/hidden-content-preferences/watchers'
import * as hideWatchedWatchers from '../services/hide-watched/watchers'
import * as inboundTrackingWatchers from '../services/inbound-tracking/watchers'
import * as interstitialWatchers from '../services/interstitial/watchers'
import * as notificationWatchers from '../services/notifications/watchers'
import * as onboardingWatchers from '../services/onboarding/watchers'
import * as plansWatchers from '../services/plans/watchers'
import * as playlistWatchers from '../services/playlist/watchers'
import * as portalWatchers from '../services/portal/watchers'
import * as pmScreenWatchers from '../services/pm-screen/watchers'
import * as pmSectionWatchers from '../services/pm-section/watchers'
import * as pmListWatchers from '../services/pm-list/watchers'
import * as referralWatchers from '../services/referral/watchers'
import * as serverTimeWatchers from '../services/server-time/watchers'
import * as shareWatchers from '../services/share/watchers'
import * as subscriptionWatchers from '../services/subscription/watchers'
import * as testarossaWatchers from '../services/testarossa/watchers'
import * as updatePaymentWatchers from '../services/update-payment/watchers'
import * as userAccountWatchers from '../services/user-account/watchers'
import * as userProfilesWatchers from '../services/user-profiles/watchers'
import * as videoWatchers from '../services/video/watchers'
import * as userWatchers from '../services/user/watchers'
import * as nodeWatchers from '../services/node/watchers'
import * as tooltipWatchers from '../services/tool-tip/watchers'
import * as tilesWatchers from '../services/tiles/watchers'
import * as everflowWatchers from '../services/everflow/watchers'
import * as communityProfileWatchers from '../services/community/identity/profile/redux/watchers'
import * as complianceManagementWatchers from '../services/compliance-management/watchers'
import * as oracleWatchers from '../services/oracle/watchers'

import { nodeReducer } from '../services/node/reducers'
import { videosRecieved } from '../services/node/index'

// special watchers that require configuration parameters are imported differently
import createNavigationWatchers from '../services/navigation/watchers'
import { SET_APP_STORE_INITIALIZED } from '../services/app/actions'

/**
 * Get the middleware for use in the store.
 * @param {Object} options The options for the function
 * @param {String} [options.enableReduxLogger] Enable Redux logger middleware
 * @param {Object} [options.history] The application history object from npm history package
 * @returns {Array} An array of middleware
 */
async function getMiddleware (options = {}) {
  const { BROWSER } = process.env
  const { enableReduxLogger = false, history } = options
  // Do not assign middleware on the server since it does nothing
  if (!BROWSER) {
    return []
  }
  // create a new Witness middleware
  const witnessMiddleware = createWitnessMiddleware()
  const navigationWatchers = createNavigationWatchers(history)
  // apply watcher functions to middleware
  witnessMiddleware.run(
    navigationWatchers,
    alertBarWatchers,
    ambassadorsWatchers,
    appWatchers,
    authWatchers,
    tooltipWatchers,
    interstitialWatchers,
    testarossaWatchers,
    userProfilesWatchers,
    onboardingWatchers,
    notificationWatchers,
    shareWatchers,
    portalWatchers,
    pmScreenWatchers,
    pmSectionWatchers,
    pmListWatchers,
    playlistWatchers,
    checkoutWatchers,
    giftWatchers,
    abuseWatchers,
    adyenWatchers,
    updatePaymentWatchers,
    emailSignupWatchers,
    eventTrackingWatchers,
    cookieWatchers,
    plansWatchers,
    dialogWatchers,
    userWatchers,
    upstreamContextWatchers,
    detailWatchers,
    referralWatchers,
    userAccountWatchers,
    filterSetWatchers,
    serverTimeWatchers,
    subscriptionWatchers,
    hideWatchedWatchers,
    hiddenContentWatchers,
    inboundTrackingWatchers,
    videoWatchers,
    nodeWatchers,
    tilesWatchers,
    everflowWatchers,
    communityProfileWatchers,
    complianceManagementWatchers,
    oracleWatchers,
  )

  const middleware = [
    authMiddleware,
    assetsMiddleware,
    witnessMiddleware,
    dispatchWhenMiddleware,
    dialogMiddleware,
    eventTrackingMiddleware,
    userMiddleware,
    optimizelyMiddleware,
    optimizelyFeatureMiddleware,
    featureTrackingMiddleware,
    paytrackMiddleware,
    searchMiddleware,
    liveAccessEventsMiddleware,
    userNodeInfoMiddleware,
    onboardingMiddleware,
    guidesMiddleware,
    guideDaysMiddleware,
  ]

  if (enableReduxLogger) {
    try {
      return _concat(middleware, logger)
    } catch (err) {
      // Do nothing just return the middleware below
    }
  }
  return middleware
}

/**
 * Prepare the redux store for the app.
 * @param {Object} options The options for the function
 * @param {Object} [options.initialState] The initial state for the store
 * @param {Object} [options.hydrate] The data to replace in initial state
 * @param {String} [options.logLevel] The logging level
 * @param {Object} [options.history] The application history object from npm history package
 * @returns {Object} A redux store instance
 */
export async function createStore (options = {}) {
  const {
    initialState = {}, hydrate = {}, history, logLevel,
  } = options
  const state = _assign({}, initialState)

  if (hydrate.menu) {
    state.menu = fromJS(hydrate.menu)
  }

  if (hydrate.resolver) {
    state.resolver = state.resolver.withMutations((mutateState) => mutateState
      .set('path', hydrate.resolver.path)
      .set('query', fromJS(hydrate.resolver.query))
      .set('location', hydrate.resolver.location) // Keep this a plain JS object
      .set('data', fromJS(hydrate.resolver.data)))
    state.header = state.header
      .set('id', state.resolver.getIn(['data', 'id']))
      .set('notificationsMenuSection', state.resolver.getIn(['query', 'section']))
  }

  if (hydrate.app) {
    state.app = fromJS(hydrate.app)
  }

  if (hydrate.interstitial) {
    state.interstitial = fromJS(hydrate.interstitial)
  }

  if (hydrate.jumbotron) {
    state.jumbotron = fromJS(hydrate.jumbotron)
  }

  if (hydrate.category) {
    state.category = fromJS(hydrate.category)
  }

  if (hydrate.userProfiles) {
    state.userProfiles = fromJS(hydrate.userProfiles)
  }

  if (hydrate.communityProfile) {
    state.communityProfile = state.communityProfile
      .mergeDeep(fromJS(hydrate.communityProfile))
  }

  if (hydrate.tiles) {
    state.tiles = fromJS(hydrate.tiles)
  }

  if (hydrate.resetPassword) {
    state.resetPassword = fromJS(hydrate.resetPassword)
  }

  if (hydrate.shareView) {
    state.shareView = fromJS(hydrate.shareView)
  }

  if (hydrate.shareData) {
    state.share = fromJS(hydrate.shareData)
  }

  if (hydrate.plans) {
    state.plans = fromJS(hydrate.plans)
  }

  if (hydrate.videos) {
    const nodeStateWithVideos = !hydrate.videos.videosData || !hydrate.videos.videoIds
      ? state.node
      : nodeReducer(
        state.node,
        videosRecieved({
          nodes: hydrate.videos.videosData,
          language: hydrate.videos.language,
        }),
      )
    state.node = nodeStateWithVideos
  }

  if (hydrate.video) {
    state.video = state.video.withMutations((mutateState) => mutateState
      .set('id', hydrate.video.id)
      .set('path', hydrate.resolver.path)
      .set('data', fromJS(hydrate.video.data)))
  }

  if (hydrate.media) {
    state.media = fromJS(hydrate.media)
  }

  if (hydrate.portal) {
    state.portal = fromJS(hydrate.portalData)
  }

  if (hydrate.auth) {
    state.auth = fromJS(hydrate.auth)
  }

  if (hydrate.userVideo) {
    state.userVideo = state.userVideo.withMutations((mutateState) => mutateState
      .set('id', hydrate.userVideo.id)
      .set('path', hydrate.resolver.path)
      .set('data', fromJS(hydrate.userVideo.data)))
  }

  if (hydrate.plans) {
    state.plans = fromJS(hydrate.plans)
  }

  if (hydrate.home) {
    state.home = state.home.set('data', fromJS(hydrate.home))
  }

  if (hydrate.tilesArticle) {
    state.tilesArticle = fromJS(hydrate.tilesArticle)
  }

  if (hydrate.serverTime) {
    state.serverTime = fromJS(hydrate.serverTime)
  }

  if (hydrate.page) {
    state.page = fromJS(hydrate.page)
  }

  if (hydrate.filterSet) {
    state.filterSet = fromJS(hydrate.filterSet)
  }

  if (hydrate.recentlyAdded) {
    state.recentlyAdded = fromJS(hydrate.recentlyAdded)
  }

  if (hydrate.article) {
    state.article = fromJS(hydrate.article)
  }

  if (hydrate.languages) {
    state.languages = fromJS(hydrate.languages)
  }

  if (hydrate.staticText) {
    state.staticText = fromJS(hydrate.staticText)
  }

  if (hydrate.featureTracking) {
    state.featureTracking = fromJS(hydrate.featureTracking)
  }

  if (hydrate.user) {
    state.user = fromJS(hydrate.user)
  }

  if (hydrate.optimizely) {
    state.optimizely = fromJS(hydrate.optimizely)
  }

  if (hydrate.optimizelyFeature) {
    state.optimizelyFeature = fromJS(hydrate.optimizelyFeature)
  }

  /**
   * If redux devtools exists and devtools are enable use the extension
   * @see https://chrome.google.com/webstore/detail/redux-devtools/lmhkpmbekcpmknklioeibfkpmmfibljd?hl=en
   */
  const LOG_LEVEL_DEBUG = logLevel === 'debug'
  const showReduxDevTools = LOG_LEVEL_DEBUG && global.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
  const showReduxConsoleLogs = LOG_LEVEL_DEBUG && !global.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
  const middleware = await getMiddleware({
    // Redux debugging will be handled by Redux logger only if Redux devtools
    // is not loaded as an extension
    enableReduxLogger: showReduxConsoleLogs,
    history,
  })

  // create the official redux store
  // Configure the store with an initial State
  const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) => getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: false,
    }).concat(middleware),
    devTools: showReduxDevTools,
    preloadedState: state,
    enhancers: (getDefaultEnhancers) => getDefaultEnhancers(),
  })
  store.dispatch({ type: SET_APP_STORE_INITIALIZED })
  return store
}
